import React, { FC, useCallback } from 'react';

import { TrashBin } from '@gravity-ui/icons';
import { Alert, Flex, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { installableAppLabels as l } from 'client/blocks/Settings/channels/labels';
import { ApkFileUploaderInput } from 'client/blocks-redesign/FilesUploaders/FilesUploaders';
import { ApkTargetPlatform } from 'client/blocks-redesign/form/ApkTargetPlatform/ApkTargetPlatform';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { ApkInfoWithTargets } from 'client/types';
import { getCanViewAsAdmin, getIsAdmin } from 'client/utils';
import * as keyset from 'i18n/ApkFormFields';
import * as settingsErrorsKeyset from 'i18n/SettingsErrors';
import moment from 'moment';

import { ApkLink } from '../ApkLink/ApkLink';

import styles from './ApkFormFields.module.scss';

interface Props {
    index: number;
    apkInfo: Partial<ApkInfoWithTargets>;
    apkInfosLength: number;
    slug: string;
    packageName?: string;
    handleUpdate: (index: number, updatedValues: Partial<ApkInfoWithTargets>) => void;
    handleDelete: (index: number) => void;
    setApkWasUpdated: React.Dispatch<React.SetStateAction<boolean[]>>;
    apkWasUpdated?: boolean;
    availableTargetPlatforms: string[];
    isApkLoading: boolean;
    setIsApkLoading: React.Dispatch<React.SetStateAction<boolean>>;
    error?: string;
    disabled: boolean;
}

const t = i18n(keyset);
const tError = i18n(settingsErrorsKeyset);

export const ApkFormField: FC<Props> = ({
    index,
    apkInfo,
    apkInfosLength,
    slug,
    packageName,
    handleUpdate,
    handleDelete,
    apkWasUpdated,
    setApkWasUpdated,
    availableTargetPlatforms,
    isApkLoading,
    setIsApkLoading,
    error,
    disabled,
}) => {
    const updateAppVersion = useCallback(
        (updatedValue: Partial<ApkInfoWithTargets>) => {
            handleUpdate(index, updatedValue);
        },
        [index, handleUpdate],
    );

    const onApkUpdate = useCallback(() => {
        setApkWasUpdated((state: boolean[]) => {
            const updatedState = [...state];
            updatedState[index] = true;
            return updatedState;
        });
    }, [setApkWasUpdated, index]);

    return (
        <>
            <Field
                required
                fieldTitle={apkInfosLength > 1 ? `${t('apk-title')} #${index + 1}` : t('apk-title')}
                fieldDescription={t('apk-description')}
                containerClassName={styles.apkFormFields__field}
            >
                <ApkFileUploaderInput
                    disabled={disabled}
                    updateAppVersion={updateAppVersion}
                    packageName={packageName}
                    versionName={apkInfo?.versionName}
                    versionNotes={apkInfo?.versionNotes}
                    onApkUpdate={onApkUpdate}
                    setIsApkLoading={setIsApkLoading}
                />
                {apkInfo.url && (
                    <ApkLink
                        url={apkInfo.url}
                        fileName={`${slug}_${apkInfo.versionName}_${apkInfo.versionCode}_downloaded_${moment(Date.now()).format('YYYY-MM-DD_HH-mm')}.apk`.replace(
                            /[^a-zA-Z0-9_-]/g,
                            '_',
                        )}
                    />
                )}
                {getIsAdmin() && index > 0 && !disabled && (
                    <Alert
                        theme="normal"
                        title={<span className={styles.apkFormFields__message}>{t('delete-apk-title')}</span>}
                        className={clx(styles.apkFormFields__alertContainer, styles.apkFormFields__element)}
                        layout="horizontal"
                        icon={<TrashBin />}
                        actions={
                            <Flex>
                                <Alert.Action
                                    onClick={() => handleDelete(index)}
                                    loading={isApkLoading}
                                    view="outlined-danger"
                                >
                                    {t('delete-apk-action')}
                                </Alert.Action>
                            </Flex>
                        }
                    />
                )}
            </Field>
            <Field
                fieldTitle={t('version-name-title')}
                fieldDescription={t('version-name-description')}
                error={''}
                size="m"
                containerClassName={styles.apkFormFields__field}
            >
                <TextInput
                    className={styles.apkFormFields__element}
                    disabled={disabled || !apkWasUpdated}
                    onChange={(e) => handleUpdate(index, { versionName: e.target.value })}
                    size="xl"
                    value={String(apkInfo?.versionName ?? '')}
                />
            </Field>
            <Field fieldTitle={t('version-title')} size="m" containerClassName={styles.apkFormFields__field}>
                <TextInput
                    className={styles.apkFormFields__element}
                    disabled
                    size="xl"
                    value={String(apkInfo?.versionCode ?? '')}
                />
            </Field>
            <Field fieldTitle={l.versionNotes} error={''} size="m" containerClassName={styles.apkFormFields__field}>
                <TextInput
                    className={styles.apkFormFields__element}
                    size="xl"
                    value={String(apkInfo.versionNotes ?? '')}
                    onChange={(e) => handleUpdate(index, { versionNotes: e.target.value })}
                    disabled={disabled || !apkWasUpdated}
                />
            </Field>
            {getCanViewAsAdmin() && (
                <ApkTargetPlatform
                    error={
                        error && error === tError('apk-empty-platform-list')
                            ? tError('apk-empty-platform-list')
                            : undefined
                    }
                    disabled={disabled || !apkInfo?.appVersionId}
                    availableTargetPlatforms={availableTargetPlatforms}
                    selectedTargetPlatforms={apkInfo?.targetPlatforms}
                    updateAppVersion={(updatedValues: Partial<ApkInfoWithTargets>) =>
                        handleUpdate(index, updatedValues)
                    }
                />
            )}
        </>
    );
};
